import { RequestQuery } from '@/data/types/RequestQuery';
import { RequestParams } from 'integration/generated/transactions/http-client';
import {
	transactionsGeonodeCustom,
	transactionsStoreLocator,
} from 'integration/generated/transactions';
import { useSettings } from '@/data/Settings';
import { getClientSideCommon } from '@/data/utils/getClientSideCommon';
import { useNextRouter } from '@/data/Content/_NextRouter';
import { useExtraRequestParameters } from '@/data/Content/_ExtraRequestParameters';
import { useCallback } from 'react';
import {
	GetInventoryStoreBody,
	StorelocatorStorelocator,
} from 'integration/generated/transactions/data-contracts';
import { useNotifications } from '@/data/Content/Notifications';
import { processError } from '@/data/utils/processError';
import { TransactionErrorResponse } from '@/data/types/Basic';

const getInventoryStoreFetcher =
	(pub: boolean) =>
	async (
		storeId: string,
		query: RequestQuery = {},
		data: GetInventoryStoreBody,
		params: RequestParams
	) => {
		const res = await transactionsGeonodeCustom(pub).getInventoryStore(
			storeId,
			query,
			data,
			params
		);
		return res;
	};

const getPhysicalStoresFetcher =
	(pub: boolean) =>
	async (storeId: string, query: RequestQuery = {}, params: RequestParams) => {
		const res = await transactionsGeonodeCustom(pub).getPhysicalStores(storeId, query, params);
		return res;
	};

const getCitiesFetcher =
	(pub: boolean) =>
	async (storeId: string, state: string, query: RequestQuery = {}, params: RequestParams) => {
		const res = await transactionsGeonodeCustom(pub).getCities(storeId, state, query, params);
		return res;
	};

const getStoresByGeoNodeFetcher =
	(pub: boolean) =>
	async (storeId: string, geoId: string, query: RequestQuery = {}, params: RequestParams) => {
		const res = await transactionsStoreLocator(pub).storeLocatorFindGeoNodeByGeoId(
			storeId,
			geoId,
			query,
			params
		);
		return res;
	};

export const usePhysicalStores = () => {
	const { settings } = useSettings();
	const router = useNextRouter();
	const { storeId } = getClientSideCommon(settings, router);
	const params = useExtraRequestParameters();
	const { notifyError } = useNotifications();

	const getCities = useCallback(
		async (uniqueIDState: string) => {
			try {
				const response = await getPhysicalStoresFetcher(true)(
					storeId,
					{ state: uniqueIDState },
					params
				);

				const responseCities = await getCitiesFetcher(true)(storeId, uniqueIDState, {}, params);

				const uniqueResponse = [...new Set(response.response)];

				let cities: any[] = [];

				if (typeof responseCities?.GeoNode != 'undefined') {
					cities = uniqueResponse.map((e) => {
						const ciudad = responseCities?.GeoNode?.find((city: any) => city.uniqueID == e);
						if (ciudad?.Description) {
							return ciudad.Description[0].shortDescription;
						}
					});
				}
				return cities || [];
			} catch (error) {
				notifyError(processError(error as TransactionErrorResponse));
				return null;
			}
		},
		[storeId, params, notifyError]
	);

	const getInventoryStores = useCallback(
		async (data: GetInventoryStoreBody) => {
			try {
				const response = await getInventoryStoreFetcher(true)(storeId, {}, data, params);
				return JSON.parse(response?.response);
			} catch (error) {
				notifyError(processError(error as TransactionErrorResponse));
				return null;
			}
		},
		[storeId, params, notifyError]
	);

	return {
		getCities,
		getInventoryStores,
	};
};

const extractUniqueCities = (geoData: StorelocatorStorelocator[]) => {
	const uniqueCities = new Set<string>();

	geoData.forEach((data) => {
		data?.PhysicalStore?.forEach((store) => {
			const originalCity = store?.city?.trim();

			const words = originalCity?.split(' ');
			const isCorrectCapitalization = words?.every(
				(word) => word.charAt(0) === word.charAt(0).toUpperCase()
			);

			if (isCorrectCapitalization) {
				uniqueCities.add(originalCity || '');
			}
		});
	});

	return Array.from(uniqueCities).filter((city) => city !== '');
};
